import { type SelectHTMLAttributes, useId } from 'react';
import { Icon } from '../icons';
import { cn } from '../../lib/utils';
import { inputVariants } from './input-variants';
import type { InputSize } from './input-variants';

export type FilterSelectOption = { id: string; label: string };

export type FilterSelectProps = Omit<
  SelectHTMLAttributes<HTMLSelectElement>,
  'onChange' | 'size'
> & {
  value: string;
  options: FilterSelectOption[];
  onChange: (id: string) => void;
  label?: string;
  selectSize?: InputSize;
};

export function FilterSelect({
  value,
  options,
  onChange,
  label,
  selectSize = 'sm',
  className,
  disabled,
  id: idProp,
  ...rest
}: FilterSelectProps) {
  const genId = useId();
  const selectId = idProp ?? genId;

  return (
    <div
      className={cn(
        'relative inline-flex min-w-0 max-w-full align-middle',
        className,
      )}
    >
      {label ? (
        <label htmlFor={selectId} className='sr-only'>
          {label}
        </label>
      ) : null}
      <select
        id={selectId}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          inputVariants({
            inputSize: selectSize,
            fullWidth: false,
            className:
              'min-w-[8rem] max-w-full cursor-pointer appearance-none pr-9 font-medium text-foreground',
          }),
        )}
        {...rest}
      >
        {options.map((opt) => (
          <option key={opt.id} value={opt.id}>
            {opt.label}
          </option>
        ))}
      </select>
      <Icon
        name='chevron-down'
        className='pointer-events-none absolute right-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground'
      />
    </div>
  );
}
